import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';
import { toast } from 'react-toastify';
import { ordersAPI } from '../services/api';

// const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://check.policypulsehq.com/api';
const FILTERS = ['Daily', 'Weekly', 'Monthly'];
const PLATFORMS = [
  { id: 'amazon',  label: 'Amazon',  color: '#E78C2E' },
  { id: 'shopify', label: 'Shopify', color: '#3B8E59' },
  { id: 'ebay',    label: 'eBay',    color: '#3A6EC8' },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function getAuthHeaders() {
  const token = localStorage.getItem('token');
  return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
}

function getDateRange(filter) {
  const today = new Date();
  const end   = today.toISOString().split('T')[0];
  let start;
  if (filter === 'Daily') {
    start = end;
  } else if (filter === 'Weekly') {
    const d = new Date(today);
    d.setDate(d.getDate() - 6);   // rolling 7 days
    start = d.toISOString().split('T')[0];
  } else {
    const d = new Date(today);
    d.setDate(d.getDate() - 29);  // rolling 30 days
    start = d.toISOString().split('T')[0];
  }
  return { start, end: end + ' 23:59:59' };
}

// ── Shared store cards UI (Amazon + eBay) ─────────────────────────────────────

function StoreCards({ platform, stores, stats, syncing, loading, total, filter, onSync }) {
  const filterLabel = filter === 'Daily' ? 'today' : filter === 'Weekly' ? 'this week' : 'this month';

  return (
    <div className="mt-6">
      <div className="flex items-center gap-2 mb-3">
        <span className="w-2.5 h-2.5 rounded-full inline-block flex-shrink-0"
          style={{ backgroundColor: platform.color }} />
        <span className="text-white font-semibold text-base">{platform.label}</span>
        <span className="text-gray-400 text-sm">
          {stores.length} {stores.length === 1 ? 'store' : 'stores'} ·{' '}
          {loading ? '…' : total.toLocaleString()} orders {filterLabel}
        </span>
      </div>

      {loading ? (
        <div className="text-gray-500 text-sm py-3 text-center">Loading…</div>
      ) : stores.length === 0 ? (
        <div className="text-gray-500 text-sm py-3 text-center">No stores configured</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {stores.map((store) => {
            const st        = stats[store.id];
            const orders    = st?.total_orders || 0;
            const isSyncing = !!syncing[store.id];
            return (
              <div key={store.id}
                className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 hover:bg-white/10 transition-colors">
                <div className="flex items-start justify-between gap-1 mb-1">
                  <p className="text-xs text-gray-400 truncate flex-1" title={store.store_name}>
                    {store.store_name}
                  </p>
                  <button
                    onClick={() => onSync(store.id)}
                    disabled={isSyncing}
                    title="Sync orders"
                    className="text-gray-500 hover:text-white transition-colors disabled:opacity-40 flex-shrink-0 mt-0.5"
                  >
                    <RefreshCw size={11} className={isSyncing ? 'animate-spin' : ''} />
                  </button>
                </div>
                <p className="text-2xl font-semibold text-white leading-tight">
                  {orders.toLocaleString()}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">orders</p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Amazon ────────────────────────────────────────────────────────────────────

const AMAZON_CHANNELS = [
  { label: 'All',  value: '' },
  { label: 'FBM',  value: 'MFN' },
  { label: 'FBA',  value: 'AFN' },
];

function AmazonSection({ filter }) {
  const [stores, setStores]     = useState([]);
  const [stats, setStats]       = useState({});
  const [syncing, setSyncing]   = useState({});
  const [loading, setLoading]   = useState(true);
  const [channel, setChannel]   = useState(''); // '' | 'MFN' | 'AFN'

  const fetchAllStats = useCallback(async (list, f, ch) => {
    const { start, end } = getDateRange(f);
    const results = await Promise.all(
      list.map(async (store) => {
        try {
          const params = new URLSearchParams({ start_date: start, end_date: end });
          if (ch) params.append('fulfillment_channel', ch);
          const res  = await fetch(`${API_BASE_URL}/amazon/${store.id}/orders/stats?${params}`, { headers: getAuthHeaders() });
          const data = await res.json();
          return [store.id, data.success ? data.stats : null];
        } catch { return [store.id, null]; }
      })
    );
    setStats(Object.fromEntries(results));
  }, []);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res  = await fetch(`${API_BASE_URL}/amazon/stores`, { headers: getAuthHeaders() });
        const data = await res.json();
        const list = data.stores || [];
        setStores(list);
        await fetchAllStats(list, filter, channel);
      } catch (e) { console.error('Amazon stores error', e); }
      finally { setLoading(false); }
    })();
  }, []);

  useEffect(() => {
    if (stores.length) fetchAllStats(stores, filter, channel);
  }, [filter, channel]);

  const handleSync = async (storeId) => {
    try {
      setSyncing(s => ({ ...s, [storeId]: true }));
      const res  = await fetch(`${API_BASE_URL}/amazon/${storeId}/orders/sync`, {
        method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({})
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Amazon: Synced ${data.synced} orders!`);
        fetchAllStats(stores, filter, channel);
      } else { toast.error('Amazon sync failed'); }
    } catch { toast.error('Amazon sync failed'); }
    finally { setSyncing(s => ({ ...s, [storeId]: false })); }
  };

  const total = Object.values(stats).reduce((s, st) => s + (st?.total_orders || 0), 0);
  const filterLabel = filter === 'Daily' ? 'today' : filter === 'Weekly' ? 'this week' : 'this month';

  return (
    <div className="mt-6">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full inline-block flex-shrink-0"
            style={{ backgroundColor: PLATFORMS[0].color }} />
          <span className="text-white font-semibold text-base">Amazon</span>
          <span className="text-gray-400 text-sm">
            {stores.length} {stores.length === 1 ? 'store' : 'stores'} ·{` `}
            {loading ? '…' : total.toLocaleString()} orders {filterLabel}
            {channel ? ` (${channel === 'MFN' ? 'FBM' : 'FBA'})` : ''}
          </span>
        </div>
        {/* FBA / FBM channel tabs */}
        <div className="flex gap-1">
          {AMAZON_CHANNELS.map(c => (
            <button key={c.value} onClick={() => setChannel(c.value)}
              className={`px-2.5 py-0.5 rounded-full text-xs font-medium border transition-all duration-150 ${
                channel === c.value
                  ? c.value === 'AFN' ? 'bg-blue-600/70 text-white border-blue-500'
                  : c.value === 'MFN' ? 'bg-orange-600/70 text-white border-orange-500'
                  : 'bg-white/15 text-white border-white/30'
                  : 'bg-transparent text-gray-400 border-white/10 hover:border-white/20 hover:text-gray-300'
              }`}>
              {c.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-gray-500 text-sm py-3 text-center">Loading…</div>
      ) : stores.length === 0 ? (
        <div className="text-gray-500 text-sm py-3 text-center">No stores configured</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {stores.map((store) => {
            const st        = stats[store.id];
            const orders    = st?.total_orders || 0;
            const isSyncing = !!syncing[store.id];
            return (
              <div key={store.id}
                className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 hover:bg-white/10 transition-colors">
                <div className="flex items-start justify-between gap-1 mb-1">
                  <p className="text-xs text-gray-400 truncate flex-1" title={store.store_name}>
                    {store.store_name}
                  </p>
                  <button onClick={() => handleSync(store.id)} disabled={isSyncing}
                    title="Sync orders"
                    className="text-gray-500 hover:text-white transition-colors disabled:opacity-40 flex-shrink-0 mt-0.5">
                    <RefreshCw size={11} className={isSyncing ? 'animate-spin' : ''} />
                  </button>
                </div>
                <p className="text-2xl font-semibold text-white leading-tight">{orders.toLocaleString()}</p>
                <p className="text-xs text-gray-500 mt-0.5">orders</p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── eBay ──────────────────────────────────────────────────────────────────────

function EbaySection({ filter }) {
  const [stores, setStores]   = useState([]);
  const [stats, setStats]     = useState({});
  const [syncing, setSyncing] = useState({});
  const [loading, setLoading] = useState(true);

  // ✅ Now correctly passes start_date & end_date — backend supports it
  const fetchAllStats = useCallback(async (list, f) => {
    const { start, end } = getDateRange(f);
    const results = await Promise.all(
      list.map(async (store) => {
        try {
          const params = new URLSearchParams({ start_date: start, end_date: end });
          const res  = await fetch(`${API_BASE_URL}/ebay/${store.id}/orders/stats?${params}`, { headers: getAuthHeaders() });
          const data = await res.json();
          return [store.id, data.success ? data.stats : null];
        } catch { return [store.id, null]; }
      })
    );
    setStats(Object.fromEntries(results));
  }, []);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res  = await fetch(`${API_BASE_URL}/ebay/stores`, { headers: getAuthHeaders() });
        const data = await res.json();
        const list = data.stores || [];
        setStores(list);
        await fetchAllStats(list, filter);
      } catch (e) { console.error('eBay stores error', e); }
      finally { setLoading(false); }
    })();
  }, []);

  useEffect(() => {
    if (stores.length) fetchAllStats(stores, filter);
  }, [filter]);

  const handleSync = async (storeId) => {
    try {
      setSyncing(s => ({ ...s, [storeId]: true }));
      const res  = await fetch(`${API_BASE_URL}/ebay/${storeId}/orders/sync`, {
        method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({})
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`eBay: Synced ${data.synced} orders!`);
        fetchAllStats(stores, filter);
      } else { toast.error('eBay sync failed'); }
    } catch { toast.error('eBay sync failed'); }
    finally { setSyncing(s => ({ ...s, [storeId]: false })); }
  };

  const total = Object.values(stats).reduce((s, st) => s + (st?.total_orders || 0), 0);

  return (
    <StoreCards
      platform={PLATFORMS[2]}
      stores={stores} stats={stats} syncing={syncing}
      loading={loading} total={total} filter={filter}
      onSync={handleSync}
    />
  );
}

// ── Shopify ───────────────────────────────────────────────────────────────────

function ShopifySection({ filter }) {
  const [stats, setStats]     = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async (f) => {
    try {
      setLoading(true);
      const { start, end } = getDateRange(f);
      const res = await ordersAPI.getStats({ start_date: start, end_date: end });
      setStats(res.data.stats);
    } catch (e) { console.error('Shopify stats error', e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchStats(filter); }, [filter]);

  const handleSync = async () => {
    try {
      setSyncing(true);
      const res = await ordersAPI.sync({});
      toast.success(`Shopify: Synced ${res.data.synced} orders!`);
      fetchStats(filter);
    } catch { toast.error('Shopify sync failed'); }
    finally { setSyncing(false); }
  };

  const orders      = stats?.total_orders || 0;
  const filterLabel = filter === 'Daily' ? 'today' : filter === 'Weekly' ? 'this week' : 'this month';

  return (
    <div className="mt-6">
      <div className="flex items-center gap-2 mb-3">
        <span className="w-2.5 h-2.5 rounded-full inline-block flex-shrink-0"
          style={{ backgroundColor: PLATFORMS[1].color }} />
        <span className="text-white font-semibold text-base">Shopify</span>
        <span className="text-gray-400 text-sm">
          {loading ? '…' : orders.toLocaleString()} orders {filterLabel}
        </span>
      </div>

      {loading ? (
        <div className="text-gray-500 text-sm py-3 text-center">Loading…</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {/* ✅ Shopify store card with sync button inside — same pattern as Amazon/eBay */}
          <div className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 hover:bg-white/10 transition-colors">
            <div className="flex items-start justify-between gap-1 mb-1">
              <p className="text-xs text-gray-400 truncate flex-1">Main Store</p>
              <button
                onClick={handleSync}
                disabled={syncing}
                title="Sync orders"
                className="text-gray-500 hover:text-white transition-colors disabled:opacity-40 flex-shrink-0 mt-0.5"
              >
                <RefreshCw size={11} className={syncing ? 'animate-spin' : ''} />
              </button>
            </div>
            <p className="text-2xl font-semibold text-white leading-tight">
              {orders.toLocaleString()}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">orders</p>
          </div>

          {stats?.total_revenue != null && (
            <div className="bg-white/5 border border-white/10 rounded-xl px-4 py-3">
              <p className="text-xs text-gray-400 mb-1">Revenue</p>
              <p className="text-xl font-semibold text-white leading-tight">
                ${parseFloat(stats.total_revenue || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────

export default function OrdersSummary() {
  const [filter, setFilter] = useState('Weekly');

  return (
    <div className="w-full max-w-6xl mx-auto mt-10 px-4">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
        <div className="flex items-center gap-3 flex-1">
          <div className="h-px flex-1 bg-white/10" />
          <span className="text-gray-400 text-xs uppercase tracking-widest whitespace-nowrap">
            Orders Overview
          </span>
          <div className="h-px flex-1 bg-white/10" />
        </div>
        <div className="flex gap-1">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition-all duration-150 ${
                filter === f
                  ? 'bg-white/15 text-white border-white/30'
                  : 'bg-transparent text-gray-400 border-white/10 hover:border-white/20 hover:text-gray-300'
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      <AmazonSection  filter={filter} />
      <div className="border-t border-white/10 mt-6" />
      <ShopifySection filter={filter} />
      <div className="border-t border-white/10 mt-6" />
      <EbaySection    filter={filter} />
    </div>
  );
}